import { Query } from '@apollo/client/react/components';
import * as React from 'react';
import { debounce } from 'lodash';

import { WithRouterProps } from 'react-router';

import BoxBody from 'components/BoxBody';
import Button from 'components/ButtonDeprecated';
import ConfirmDialog from 'components/ConfirmDialog';
import CurrentUser from 'auth/CurrentUser';
import Dialog from 'components/Dialog';
import Divider from 'components/Divider';
import enumerate from 'lib/enumerate';
import errorLogger from 'utils/errorLogger';
import ErrorReporter from 'app/components/ErrorReporter';
import Group from 'entities/Group';
import GroupActions from 'groups/actions/GroupActions';
import GroupAvatarSelector from './GroupAvatarSelector';
import GroupForm from './GroupForm';
import GroupOptionsForm, {
  defaultGroupOptionsValues,
  GroupOptionsField,
  GroupOptionsValues,
} from './GroupOptionsForm/GroupOptionsForm';
import GroupRequestApprovalModal from './GroupRequestApprovalModal';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import SETTINGS_ROUTES from 'UserSettings/_shared/SettingsRouteConstants';
import ToastActions from 'actions/ToastActions';
import { withRouter } from 'app/context/Router';
import {
  LoadUserOrganizationsForGroupCreateOrEdit,
  LoadUserOrganizationsForGroupCreateOrEdit_me_organizations as Organization,
} from 'organizations/queries/__generated__/LoadUserOrganizationsForGroupCreateOrEdit';

import LOAD_USER_ORGANIZATIONS_FOR_GROUP_CREATE_OR_EDIT from 'organizations/queries/UserOrganizationsForGroupEditOrCreate.gql';
import LoadingIndicator from 'components/LoadingIndicator';

const ROLES_ALLOWED_TO_CREATE_U13 = ['admin', 'teacher', 'staff'];

const canCreateU13ForOrganization = (organization?: Organization) => {
  if (!organization) {
    return false;
  }
  const roles = organization.userAffiliation.roles as string[];
  return ROLES_ALLOWED_TO_CREATE_U13.some((role) => roles.includes(role));
};

const DIALOG_STATUS = enumerate(['CREATE_CLASS', 'REQUEST_CLASS_APPROVAL']);

export type Props = {
  onCreate?: ((group: Group) => void) | null;
  onRequestHide: () => void;
} & WithRouterProps;

type State = {
  groupOptions: GroupOptionsValues;
  code: string | undefined | null;
  name: string | undefined | null;
  avatarHandpicked: boolean;
  groupCodeHandpicked: boolean;
  suggestedGroupCode: boolean;
  canSubmit: boolean;
  dialogShown: typeof DIALOG_STATUS;
  classToRequest: Group;
  selectedAvatarUrl: string;
  selectedAvatarId: number | null;
  requestApprovalReasonText: string | null;
  classRequestButtonEnabled: boolean;
  selectedOrganization: Organization | undefined;
};

class GroupCreateModal extends React.Component<Props, State> {
  state: State = {
    groupOptions: defaultGroupOptionsValues({ hasChildren: false }),
    code: null,
    name: null,
    selectedOrganization: undefined,
    avatarHandpicked: false,
    groupCodeHandpicked: false,
    suggestedGroupCode: false,
    canSubmit: false,
    dialogShown: DIALOG_STATUS.CREATE_CLASS,
    classToRequest: new Group(),
    selectedAvatarUrl: '',
    selectedAvatarId: null,
    requestApprovalReasonText: null,
    classRequestButtonEnabled: false,
  };

  // @ts-expect-error ts-migrate(2739) FIXME: Type 'RefObject<unknown>' is missing the following... Remove this comment to see the full error message
  avatarSelector: React.ReactElement<typeof GroupAvatarSelector> | undefined | null =
    React.createRef();

  groupFormRef: React.RefObject<GroupForm> | undefined = React.createRef();

  cancelRequestApproval = () => {
    this.setState({ dialogShown: DIALOG_STATUS.CREATE_CLASS });
  };

  // @ts-expect-error ts-migrate(7006) FIXME: Parameter 'requestReasonText' implicitly has an 'a... Remove this comment to see the full error message
  handleApprovalReasonInput = (requestReasonText) => {
    this.setState({
      requestApprovalReasonText: requestReasonText,
      classRequestButtonEnabled: requestReasonText.length >= 3,
    });
  };

  suggestGroupCode = debounce((name: string) => {
    Group.suggestCode(name)
      .then((codes) => {
        if (!this.state.code && codes && codes[0]) {
          this.setState({ code: codes[0] });
          this.groupFormRef?.current?.setState({ codeFromField: codes[0] });
        }
        this.setState({ suggestedGroupCode: true });
      })
      .catch(errorLogger('GroupCreateModal suggestCode failed'));
  }, 300);

  suggestAvatarForName = debounce((name: string) => {
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'current' does not exist on type 'ReactEl... Remove this comment to see the full error message
    if (!this || !this.avatarSelector || !this.avatarSelector.current) {
      return;
    }
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'current' does not exist on type 'ReactEl... Remove this comment to see the full error message
    this.avatarSelector.current.searchAvatarForName(name);
  }, 300);

  handleAvatarSelected = () => {
    this.setState({ avatarHandpicked: true });
  };

  handleCodeChange = () => {
    if (!this.state.groupCodeHandpicked) {
      this.setState({ groupCodeHandpicked: true });
    }
  };

  handleOptionsChange = (name: GroupOptionsField, value: GroupOptionsValues[GroupOptionsField]) => {
    this.setState((prev) => ({
      groupOptions: {
        ...prev.groupOptions,
        [name]: value,
      },
    }));
  };

  handleSubmit = (gateClassesFromCurrentUser: boolean) => {
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'current' does not exist on type 'ReactEl... Remove this comment to see the full error message
    if (!this || !this.avatarSelector || !this.avatarSelector.current) {
      return;
    }

    const { groupOptions, selectedOrganization } = this.state;
    const canCreateU13 = canCreateU13ForOrganization(selectedOrganization);
    const orgData = selectedOrganization
      ? {
          organization: {
            id: selectedOrganization.id,
          },
        }
      : {};

    const groupToCreate = new Group({
      name: this.state.code,
      class_name: this.state.name,
      group_avatar: {
        // @ts-expect-error ts-migrate(2339) FIXME: Property 'current' does not exist on type 'ReactEl... Remove this comment to see the full error message
        id: this.avatarSelector.current.getSelectedAvatarId(),
      },
      // Options owned by this modal. REST create expects boolean searchable.
      has_children: canCreateU13 && groupOptions.hasChildren,
      searchable: true,
      ...orgData,
    });

    if (gateClassesFromCurrentUser) {
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'current' does not exist on type 'ReactEl... Remove this comment to see the full error message
      const selectedAvatar = this.avatarSelector && this.avatarSelector.current.getSelectedAvatar();

      this.setState({
        dialogShown: DIALOG_STATUS.REQUEST_CLASS_APPROVAL,
        classToRequest: groupToCreate,
        selectedAvatarId: selectedAvatar?.attributes.id,
        selectedAvatarUrl: selectedAvatar?.attributes.svg_url,
      });
    } else {
      // @ts-expect-error ts-migrate(2554) FIXME: Expected 2 arguments, but got 1.
      GroupActions.create(groupToCreate)
        .then((group) => {
          this.props.onRequestHide();
          if (this.props.onCreate) {
            this.props.onCreate(group);
          }
        })
        .catch(ToastActions.showAPIError);
    }
  };

  handleFormInvalid = () => {
    // Prevent excessive re-renders
    if (this.state.canSubmit) {
      this.setState({ canSubmit: false });
    }
  };

  handleFormValid = (name: string, code: string, selectedOrg?: Organization) => {
    this.setState({
      name,
      code,
      // TODO -- add a test for this (to avoid future p0s)
      // if the orgId is equal to the UNAFFILIATED_CLASS_ID, then we need to set this key to null,
      // otherwise pass in the org id
      // if the orgId is undefined for some god forsaken reason then use that in the key so that it doesn't get sent to r101-api as null and
      //   remove us from the org
      selectedOrganization: selectedOrg,
      canSubmit: true,
    });
  };

  handleSchoolChange = (selectedOrganization?: Organization) => {
    this.setState({
      selectedOrganization,
    });
  };

  handleValidNameChange = (name: string) => {
    if (!this.state.groupCodeHandpicked && !this.state.suggestedGroupCode) {
      this.suggestGroupCode(name);
    }

    if (!this.state.avatarHandpicked) {
      this.suggestAvatarForName(name);
    }
  };

  renderSubmitButton(selectedOrganization?: Organization) {
    // @ts-expect-error ts-migrate(7006) FIXME: Parameter 'props' implicitly has an 'any' type.
    const submitButtonElement = (props) => (
      <Button
        className="GroupCreateModal-submitButton"
        data-test-selector="group-create-modal-submit"
        {...props}
      >
        Create
      </Button>
    );

    if (!selectedOrganization) {
      return submitButtonElement({
        // When no org is selected and the hasChildren is true disable the creation button!
        // we don't allow creating a class without an org  where children can be added
        disabled: this.state.groupOptions.hasChildren ? true : !this.state.canSubmit,
        onClick: () => this.handleSubmit(false),
      });
    } else {
      let gateClasses = true; // Set to true, error on side of caution
      if (selectedOrganization) {
        gateClasses = selectedOrganization.approvalSettings.gateClassesFromCurrentUser;
      }
      return submitButtonElement({
        disabled: !this.state.canSubmit,
        onClick: () => this.handleSubmit(gateClasses),
      });
    }
  }

  handleSubmitRequestClassApproval = () => {
    const { classToRequest, requestApprovalReasonText } = this.state;
    GroupActions.create(classToRequest, requestApprovalReasonText)
      .then((group) => {
        this.props.onRequestHide();
        if (this.props.onCreate) {
          this.props.onCreate(group);
        }
      })
      .catch(ToastActions.showAPIError);
  };

  render() {
    if (!CurrentUser.isSignedIn()) {
      return <ErrorReporter error="GroupCreateModal - user is not signed in" />;
    }

    const user = CurrentUser.getUser();

    if (user && !user.hasEmail()) {
      return (
        <ConfirmDialog
          title="Add an email address to your account first"
          subtext="An email address is required to create a class. You can add one in your account settings."
          actionButtonText="Go to Settings"
          cancelButtonText="Cancel"
          onRequestHide={this.props.onRequestHide}
          onConfirm={() => this.props.router.push(SETTINGS_ROUTES.NOTIFICATIONS)}
        />
      );
    }

    const { onRequestHide } = this.props;
    const {
      code,
      classToRequest,
      classRequestButtonEnabled,
      dialogShown,
      groupOptions,
      name,
      selectedAvatarId,
      selectedAvatarUrl,
      selectedOrganization,
    } = this.state;

    if (dialogShown === DIALOG_STATUS.REQUEST_CLASS_APPROVAL) {
      return (
        <GroupRequestApprovalModal
          classAction="create"
          classRequestButtonEnabled={classRequestButtonEnabled}
          classToRequest={classToRequest}
          onChange={this.handleApprovalReasonInput}
          onClickCancel={this.cancelRequestApproval}
          onRequestHide={onRequestHide}
          onSubmit={this.handleSubmitRequestClassApproval}
          orgName={selectedOrganization?.name ?? ''}
          selectedAvatarUrl={selectedAvatarUrl}
        />
      );
    }

    const groupFormProps = selectedOrganization
      ? {
          currentOrganization: {
            value: selectedOrganization.id.toString(),
            label: selectedOrganization.name,
          },
        }
      : {};

    return (
      <Dialog
        {...this.props}
        eventScreenName="GroupCreateModal"
        className="GroupCreateModal"
        title="Create a class"
        size="small"
      >
        <BoxBody divided>
          <MediaObject className="GroupCreateModal-top">
            <div>
              <GroupAvatarSelector
                ref={this.avatarSelector}
                // @ts-expect-error ts-migrate(2322) FIXME: Type '{ id: number; } | null' is not assignable to... Remove this comment to see the full error message
                initialAvatar={selectedAvatarId ? { id: selectedAvatarId } : null}
                // @ts-expect-error ts-migrate(2322) FIXME: Type '() => void' is not assignable to type '(a: A... Remove this comment to see the full error message
                onSelect={this.handleAvatarSelected}
              />
            </div>
            <div>
              <Query<LoadUserOrganizationsForGroupCreateOrEdit>
                query={LOAD_USER_ORGANIZATIONS_FOR_GROUP_CREATE_OR_EDIT}
                fetchPolicy="network-only"
              >
                {({ loading, error, data }) => {
                  if (loading) {
                    return <LoadingIndicator />;
                  }

                  if (error) {
                    ToastActions.showErrorToast(error.message);
                    return null;
                  }

                  let orgOptions: Organization[] = [];
                  if (data && data.me) {
                    orgOptions = data.me?.organizations;
                  }

                  const canCreateU13 = canCreateU13ForOrganization(selectedOrganization);
                  return (
                    <div>
                      <GroupForm
                        ref={this.groupFormRef}
                        name={name || ''}
                        code={code || ''}
                        onFormValid={this.handleFormValid}
                        onValidNameChange={this.handleValidNameChange}
                        onCodeChange={this.handleCodeChange}
                        onFormInvalid={this.handleFormInvalid}
                        orgOptions={orgOptions}
                        hasChildren={groupOptions.hasChildren}
                        showOrg
                        isOnCreateModal
                        onSchoolChange={this.handleSchoolChange}
                        {...groupFormProps}
                      />
                      <Row>
                        <GroupOptionsForm
                          values={groupOptions}
                          onChange={this.handleOptionsChange}
                          canCreateU13={canCreateU13}
                        />
                      </Row>
                    </div>
                  );
                }}
              </Query>
            </div>
          </MediaObject>
          <Divider size="medium" />
          {this.renderSubmitButton(selectedOrganization)}
        </BoxBody>
      </Dialog>
    );
  }
}

export default withRouter(GroupCreateModal);
